/**
 * Independent Formatter Initializer
 * 
 * Provides completely independent initialization for the Webflow field formatting
 * system. This initializer operates separately from existing lead tracking systems
 * and can be safely deployed alongside them without interference.
 */

import {
  FormatterModuleConfig,
  FormattedFieldElement,
  FieldFormattingConfig,
  FormattingFieldType,
  FormattingError,
  FormattingErrorType,
  FormattingEvent,
  FormattingEventCallback
} from './types/formatting-types';

import {
  onDOMReady,
  findFormFieldsWithAttribute,
  isValidFormField,
  getDataAttribute,
  addEventListenerSafe,
  addCSSClass,
  removeCSSClass,
  debounce
} from './utils';

// Import new format engine and field detection systems
import { getFormatEngine, FormatEngine } from './format-engine';
import { getFieldDetector, FieldDetector } from './field-detector';
import { getFieldRegistry, FieldRegistry } from './field-registry';
import { FieldType } from './detection-patterns';

// Import validation enhancements
import { getValidationEnhancements, ValidationEnhancements, initializeValidationEnhancements } from './validation/validation-enhancements';

/**
 * Default configuration for the formatter module
 */
const DEFAULT_CONFIG: FormatterModuleConfig = {
  debug: false,
  cssClasses: {
    valid: 'formatting-valid',
    invalid: 'formatting-invalid',
    warning: 'formatting-warning'
  },
  autoScan: true,
  autoScanSelectors: [
    '[data-format-type]',
    '[data-formatting]',
    'input[data-name]', // Common Webflow pattern
    'textarea[data-name]'
  ],
  gracefulDegradation: true
};

/**
 * Main formatter initializer class
 * 
 * This class handles the complete lifecycle of the formatting module,
 * from initialization through field discovery and event management.
 * Now integrated with the comprehensive format engine and field detection systems.
 */
export class FormatterInitializer {
  private isInitialized = false;
  private config: FormatterModuleConfig;
  private trackedFields: Map<HTMLElement, FormattedFieldElement> = new Map();
  private eventListeners: Map<HTMLElement, (() => void)[]> = new Map();
  private debugLogger: (message: string, ...args: any[]) => void;
  
  // New integrated systems
  private formatEngine: FormatEngine;
  private fieldDetector: FieldDetector;
  private fieldRegistry: FieldRegistry;
  private validationEnhancements: ValidationEnhancements;

  /**
   * Create a new formatter initializer
   * @param config Optional configuration overrides
   */
  constructor(config: Partial<FormatterModuleConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.debugLogger = this.config.debug 
      ? (message: string, ...args: any[]) => console.log(`[FormatterInit] ${message}`, ...args)
      : () => {}; // No-op function when debug is disabled
    
    // Initialize integrated systems
    this.formatEngine = getFormatEngine({
      debug: this.config.debug,
      enableProgressiveFormatting: true,
      enableValidation: true,
      formatThrottle: 50,
      enableErrorRecovery: this.config.gracefulDegradation,
      cssClasses: {
        formatting: 'formatting-active',
        valid: this.config.cssClasses?.valid || 'formatting-valid',
        invalid: this.config.cssClasses?.invalid || 'formatting-invalid',
        warning: this.config.cssClasses?.warning || 'formatting-warning'
      }
    });
    
    this.fieldDetector = getFieldDetector({
      debugMode: this.config.debug,
      minConfidence: 0.3,
      enableFallbackDetection: true,
      enableDynamicDetection: true,
      gracefulDegradation: this.config.gracefulDegradation
    });
    
    this.fieldRegistry = this.fieldDetector.getRegistry();
    
    // Initialize validation enhancements with PRD requirements
    this.validationEnhancements = getValidationEnhancements({
      enableRequiredFieldIndicators: true,
      enableEnhancedPlaceholders: true,
      enableRealTimeValidation: true,
      enableSmartNextButton: true,
      nextButtonSelector: '[data-name="next"], .next-button, button[type="submit"]',
      requiredFieldSelector: '[required], [data-required="true"], [aria-required="true"]'
    });
  }

  /**
   * Initialize the formatting system
   * 
   * This method is safe to call multiple times - it will only initialize once.
   * It operates independently of any existing systems and performs graceful
   * error handling if initialization fails.
   */
  public initialize(): void {
    console.log('🔍 FormatterInitializer.initialize() called');
    
    if (this.isInitialized) {
      console.log('⏭️ Already initialized, skipping');
      this.debugLogger('Already initialized, skipping');
      return;
    }

    try {
      console.log('🔍 Starting formatter initialization');
      this.debugLogger('Starting formatter initialization');
      
      // Initialize the format engine
      this.formatEngine.initialize();
      
      // Initialize validation enhancements (PRD requirements)
      console.log('🔄 About to initialize validation enhancements...');
      try {
        this.validationEnhancements.initialize();
        console.log('✅ Validation enhancements call completed');
        this.debugLogger('Validation enhancements initialized with PRD requirements');
      } catch (validationError) {
        console.error('❌ Validation initialization failed:', validationError);
        throw validationError; // Re-throw to be caught by main try-catch
      }
      
      // Scan for fields using the comprehensive field detector
      console.log('🔍 About to scan for fields, autoScan:', this.config.autoScan);
      if (this.config.autoScan) {
        this.scanWithFieldDetector();
      } else {
        console.log('⏭️ AutoScan is disabled, skipping field detection');
      }

      // Set up mutation observer to catch dynamically added forms
      this.setupMutationObserver();

      this.isInitialized = true;
      this.debugLogger('Formatter initialization complete');

      // Get statistics from integrated systems
      const stats = this.formatEngine.getStats();
      this.debugLogger(`Initialization stats: ${stats.fieldsRegistered} fields, ${stats.formattersAvailable} formatters`);

      // Dispatch initialization event
      this.dispatchEvent('init', null, { 
        fieldsFound: stats.fieldsRegistered,
        formattersAvailable: stats.formattersAvailable,
        config: this.config,
        supportedFieldTypes: stats.supportedFieldTypes
      });

    } catch (error) {
      this.handleInitializationError(error);
    }
  }

  /**
   * Scan using the comprehensive field detector
   * 
   * This method uses the new field detection system to automatically
   * identify and configure fields for formatting.
   */
  private scanWithFieldDetector(): void {
    try {
      this.debugLogger('🔍 Scanning with comprehensive field detector');

      // Perform comprehensive field detection scan
      const scanResult = this.fieldDetector.scanDocument();
      
      this.debugLogger(`🔍 Field detection complete: ${scanResult.fieldsDetected}/${scanResult.fieldsScanned} fields detected in ${scanResult.scanDuration}ms`);
      
      if (scanResult.errors.length > 0) {
        this.debugLogger('Field detection errors:', scanResult.errors);
      }

      // Log detection statistics
      const detectionsByType = Object.entries(scanResult.detectionsByType)
        .filter(([_, count]) => count > 0)
        .map(([type, count]) => `${type}: ${count}`)
        .join(', ');
      
      if (detectionsByType) {
        this.debugLogger(`Fields detected by type: ${detectionsByType}`);
      }

      // Get all detected fields from the registry
      const detectedFields = this.fieldRegistry.getAllFields();
      this.debugLogger(`🔍 Retrieved ${detectedFields.length} fields from registry`);
      
      // Set up formatting for each detected field
      detectedFields.forEach(fieldInfo => {
        this.debugLogger(`🔍 Setting up field: ${fieldInfo.element.name || fieldInfo.element.id || 'unnamed'}`);
        this.setupFormattingForDetectedField(fieldInfo);
        
        // Apply validation enhancements to the field
        this.validationEnhancements.addField(fieldInfo);
      });

      // CRITICAL: Set up a periodic re-scan for multi-step forms
      // This will catch fields that appear after form progression
      this.setupPeriodicFieldScan();

    } catch (error) {
      this.debugLogger('Error during field detection scan:', error);
      if (!this.config.gracefulDegradation) {
        throw error;
      }
    }
  }

  /**
   * Set up periodic field scanning for multi-step forms
   * This catches fields that appear after form progression
   */
  private setupPeriodicFieldScan(): void {
    this.debugLogger('🔍 Setting up periodic field scan for multi-step forms');
    
    let scanCount = 0;
    const maxScans = 10; // Stop after 10 scans to avoid infinite scanning
    
    const periodicScan = () => {
      scanCount++;
      this.debugLogger(`🔍 Periodic scan #${scanCount}`);
      
      // Get current field count before scan
      const beforeCount = this.fieldRegistry.getAllFields().length;
      
      // Perform scan
      const scanResult = this.fieldDetector.scanDocument();
      
      // Get field count after scan
      const afterCount = this.fieldRegistry.getAllFields().length;
      const newFields = afterCount - beforeCount;
      
      if (newFields > 0) {
        this.debugLogger(`🔍 Found ${newFields} new fields in periodic scan`);
        
        // Set up new fields
        const allFields = this.fieldRegistry.getAllFields();
        const recentFields = allFields.slice(-newFields); // Get the newly added fields
        
        recentFields.forEach(fieldInfo => {
          this.debugLogger(`🔍 Setting up new field: ${fieldInfo.element.name || fieldInfo.element.id || 'unnamed'}`);
          this.setupFormattingForDetectedField(fieldInfo);
          this.validationEnhancements.addField(fieldInfo);
        });
      }
      
      // Continue scanning if we haven't reached max and might find more fields
      if (scanCount < maxScans) {
        setTimeout(periodicScan, 2000); // Scan every 2 seconds
      } else {
        this.debugLogger('🔍 Periodic scanning complete');
      }
    };
    
    // Start first periodic scan after a short delay
    setTimeout(periodicScan, 1000);
  }

  /**
   * Set up formatting for a field detected by the field detector
   * @param fieldInfo Field information from the detector
   */
  private setupFormattingForDetectedField(fieldInfo: any): void {
    try {
      const element = fieldInfo.element;
      
      // Check if already tracking this field
      if (this.trackedFields.has(element)) {
        return;
      }

      // Convert detected field type to formatting configuration
      const config: FieldFormattingConfig = {
        type: this.mapFieldTypeToFormattingType(fieldInfo.fieldType),
        realTime: fieldInfo.detection.requiresFormatting,
        validate: true,
        validationRules: [],
        options: {}
      };

      // Create formatted field element
      const formattedField: FormattedFieldElement = {
        element: element as HTMLInputElement | HTMLTextAreaElement,
        config,
        formattedValue: fieldInfo.currentValue,
        rawValue: fieldInfo.currentValue,
        isValid: fieldInfo.isValid
      };

      // Add to tracking
      this.trackedFields.set(element, formattedField);

      // Set up event listeners for format engine integration
      this.setupFormattingEventListeners(formattedField);

      this.debugLogger(`Set up formatting for ${fieldInfo.fieldType} field with confidence ${fieldInfo.detection.confidence}`);

    } catch (error) {
      this.debugLogger('Error setting up formatting for detected field:', error);
      if (!this.config.gracefulDegradation) {
        throw error;
      }
    }
  }

  /**
   * Map detected field type to formatting type
   * @param fieldType The detected field type
   * @returns Corresponding formatting type
   */
  private mapFieldTypeToFormattingType(fieldType: FieldType): FormattingFieldType {
    const mapping: Record<FieldType, FormattingFieldType> = {
      [FieldType.PHONE]: 'phone',
      [FieldType.ZIP]: 'zip',
      [FieldType.DATE_OF_BIRTH]: 'date_of_birth',
      [FieldType.CURRENCY]: 'currency',
      [FieldType.SSN]: 'ssn',
      [FieldType.UNKNOWN]: 'text'
    };

    return mapping[fieldType] || 'text';
  }

  /**
   * Attach formatting behavior to a specific form field
   * @param element The form field element to format
   */
  private attachToFormField(element: HTMLElement): void {
    try {
      if (!isValidFormField(element)) {
        this.debugLogger('Element is not a valid form field:', element);
        return;
      }

      // Check if already tracking this field
      if (this.trackedFields.has(element)) {
        this.debugLogger('Field already being tracked:', element);
        return;
      }

      // Extract formatting configuration from data attributes
      const config = this.extractFormattingConfig(element);
      if (!config) {
        this.debugLogger('No valid formatting configuration found for field:', element);
        return;
      }

      // Create formatted field element
      const formattedField: FormattedFieldElement = {
        element: element as HTMLInputElement | HTMLTextAreaElement,
        config,
        formattedValue: '',
        rawValue: '',
        isValid: true
      };

      // Add to tracking
      this.trackedFields.set(element, formattedField);

      // Set up event listeners
      this.setupFieldEventListeners(formattedField);

      this.debugLogger(`Attached formatting to field with type: ${config.type}`, element);

    } catch (error) {
      this.debugLogger('Error attaching to form field:', error);
      if (!this.config.gracefulDegradation) {
        throw error;
      }
    }
  }

  /**
   * Extract formatting configuration from element data attributes
   * @param element The form field element
   * @returns Formatting configuration or null if not found
   */
  private extractFormattingConfig(element: HTMLElement): FieldFormattingConfig | null {
    try {
      // Look for format type in various attributes
      let formatType = getDataAttribute(element, 'format-type') ||
                      getDataAttribute(element, 'formatting') ||
                      getDataAttribute(element, 'format');

      if (!formatType) {
        // Try to infer from common Webflow field names
        const fieldName = getDataAttribute(element, 'name');
        formatType = this.inferFormatTypeFromName(fieldName);
      }

      if (!formatType || !this.isValidFormatType(formatType)) {
        return null;
      }

      // Build configuration object
      const config: FieldFormattingConfig = {
        type: formatType as FormattingFieldType,
        realTime: getDataAttribute(element, 'format-realtime') === 'true',
        validate: getDataAttribute(element, 'format-validate') !== 'false', // Default to true
        validationRules: [],
        options: this.extractFormattingOptions(element)
      };

      return config;

    } catch (error) {
      this.debugLogger('Error extracting formatting configuration:', error);
      return null;
    }
  }

  /**
   * Infer format type from common field names
   * @param fieldName The field name to analyze
   * @returns Inferred format type or null
   */
  private inferFormatTypeFromName(fieldName: string | null): string | null {
    if (!fieldName) return null;

    const name = fieldName.toLowerCase();
    
    if (name.includes('phone') || name.includes('tel')) return 'phone';
    if (name.includes('email')) return 'email';
    if (name.includes('income') || name.includes('salary') || name.includes('amount')) return 'currency';
    if (name.includes('percent') || name.includes('rate')) return 'percentage';
    if (name.includes('date')) return 'date';
    if (name.includes('number') || name.includes('num')) return 'number';

    return null;
  }

  /**
   * Check if a format type is valid
   * @param formatType The format type to check
   * @returns Boolean indicating validity
   */
  private isValidFormatType(formatType: string): boolean {
    const validTypes: FormattingFieldType[] = [
      'phone', 'currency', 'percentage', 'number', 
      'decimal', 'text', 'email', 'date'
    ];
    return validTypes.includes(formatType as FormattingFieldType);
  }

  /**
   * Extract formatting options from element data attributes
   * @param element The form field element
   * @returns Formatting options object
   */
  private extractFormattingOptions(element: HTMLElement): any {
    const options: any = {};

    // Extract common options
    const symbol = getDataAttribute(element, 'format-symbol');
    if (symbol) options.currencySymbol = symbol;

    const decimals = getDataAttribute(element, 'format-decimals');
    if (decimals) options.decimalPlaces = parseInt(decimals, 10);

    const pattern = getDataAttribute(element, 'format-pattern');
    if (pattern) options.maskPattern = pattern;

    const country = getDataAttribute(element, 'format-country');
    if (country) options.defaultCountryCode = country;

    return options;
  }

  /**
   * Set up event listeners for a formatted field using format engine
   * @param formattedField The formatted field configuration
   */
  private setupFormattingEventListeners(formattedField: FormattedFieldElement): void {
    const element = formattedField.element;
    const listeners: (() => void)[] = [];

    // Progressive formatting on input
    const inputHandler = () => {
      this.formatEngine.formatField(element, formattedField.config.options, true);
    };

    // Final formatting and validation on blur
    const blurHandler = () => {
      const result = this.formatEngine.formatField(element, formattedField.config.options, false);
      this.updateFormattedFieldFromResult(formattedField, result);
    };

    // Focus handler for special cases (like SSN unmasking)
    const focusHandler = () => {
      this.handleFieldFocus(formattedField);
    };

    // Set up event listeners
    if (formattedField.config.realTime) {
      const inputCleanup = addEventListenerSafe(element, 'input', inputHandler);
      if (inputCleanup) listeners.push(inputCleanup);
    }

    const blurCleanup = addEventListenerSafe(element, 'blur', blurHandler);
    if (blurCleanup) listeners.push(blurCleanup);

    const focusCleanup = addEventListenerSafe(element, 'focus', focusHandler);
    if (focusCleanup) listeners.push(focusCleanup);

    // Store cleanup functions
    this.eventListeners.set(element, listeners);
  }

  /**
   * Update formatted field element from format operation result
   * @param formattedField The formatted field to update
   * @param result Format operation result
   */
  private updateFormattedFieldFromResult(formattedField: FormattedFieldElement, result: any): void {
    formattedField.formattedValue = result.formattedValue;
    formattedField.rawValue = result.rawValue;
    formattedField.isValid = result.validation?.isValid ?? true;
    formattedField.lastValidation = result.validation;
  }

  /**
   * Handle field formatting using format engine
   * @param formattedField The formatted field to process
   */
  private handleFieldFormatting(formattedField: FormattedFieldElement): void {
    try {
      const result = this.formatEngine.formatField(
        formattedField.element,
        formattedField.config.options,
        true // Progressive formatting
      );
      
      this.updateFormattedFieldFromResult(formattedField, result);
      
      this.debugLogger(`Formatted ${formattedField.config.type} field: ${result.success ? 'success' : 'failed'}`);
      
      this.dispatchEvent('format', formattedField, {
        type: formattedField.config.type,
        result
      });
    } catch (error) {
      this.debugLogger('Error during field formatting:', error);
    }
  }

  /**
   * Handle field validation using format engine
   * @param formattedField The formatted field to validate
   */
  private handleFieldValidation(formattedField: FormattedFieldElement): void {
    try {
      const validation = this.formatEngine.validateField(
        formattedField.element,
        formattedField.config.options
      );
      
      formattedField.isValid = validation.isValid;
      formattedField.lastValidation = validation;
      
      this.debugLogger(`Validated ${formattedField.config.type} field: ${validation.isValid ? 'valid' : 'invalid'}`);
      if (!validation.isValid) {
        this.debugLogger(`Validation errors: ${validation.errors.join(', ')}`);
      }
      
      this.dispatchEvent('validate', formattedField, {
        type: formattedField.config.type,
        validation
      });
    } catch (error) {
      this.debugLogger('Error during field validation:', error);
    }
  }

  /**
   * Set up event listeners for a formatted field
   * @param formattedField The formatted field to set up listeners for
   */
  private setupFieldEventListeners(formattedField: FormattedFieldElement): void {
    try {
      const element = formattedField.element;
      
      // Set up input event listener for real-time formatting
      const handleInput = (event: Event) => {
        this.handleFieldInput(formattedField, event);
      };

      // Set up focus event listener
      const handleFocus = () => {
        this.handleFieldFocus(formattedField);
      };

      // Set up blur event listener for final validation
      const handleBlur = () => {
        this.handleFieldValidation(formattedField);
      };

      element.addEventListener('input', handleInput);
      element.addEventListener('focus', handleFocus);
      element.addEventListener('blur', handleBlur);

      // Store cleanup function
      formattedField.cleanup = () => {
        element.removeEventListener('input', handleInput);
        element.removeEventListener('focus', handleFocus);
        element.removeEventListener('blur', handleBlur);
      };

      this.debugLogger(`Event listeners set up for ${formattedField.config.type} field`);
    } catch (error) {
      this.debugLogger('Error setting up field event listeners:', error);
    }
  }

  /**
   * Handle field input event
   * @param formattedField The formatted field that received input
   * @param event The input event
   */
  private handleFieldInput(formattedField: FormattedFieldElement, event: Event): void {
    try {
      this.debugLogger(`Field input: ${formattedField.config.type}`);
      
      // Apply real-time formatting if enabled
      if (this.config.enableRealTimeFormatting) {
        this.handleFieldFormatting(formattedField);
      }
      
      // Dispatch input event
      this.dispatchEvent('input', formattedField, {
        type: formattedField.config.type,
        originalEvent: event
      });
    } catch (error) {
      this.debugLogger('Error during field input:', error);
    }
  }

  /**
   * Handle field focus event
   * @param formattedField The formatted field that received focus
   */
  private handleFieldFocus(formattedField: FormattedFieldElement): void {
    try {
      this.debugLogger(`Field focused: ${formattedField.config.type}`);
      
      // Remove any validation styling temporarily
      const element = formattedField.element;
      if (this.config.cssClasses) {
        removeCSSClass(element, this.config.cssClasses.invalid || '');
        removeCSSClass(element, this.config.cssClasses.warning || '');
      }
    } catch (error) {
      this.debugLogger('Error during field focus:', error);
    }
  }

  /**
   * Set up mutation observer to catch dynamically added forms
   */
  private setupMutationObserver(): void {
    try {
      if (typeof MutationObserver === 'undefined') {
        this.debugLogger('MutationObserver not available, skipping dynamic form detection');
        return;
      }

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              
              // Check if the added element itself is a form field
              if (isValidFormField(element)) {
                this.attachToFormField(element);
              }
              
              // Check for form fields within the added element
              const childFields = element.querySelectorAll('input, textarea');
              childFields.forEach((field) => {
                if (isValidFormField(field)) {
                  this.attachToFormField(field as HTMLElement);
                }
              });
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      this.debugLogger('Mutation observer set up for dynamic form detection');

    } catch (error) {
      this.debugLogger('Error setting up mutation observer:', error);
    }
  }

  /**
   * Handle initialization errors gracefully
   * @param error The error that occurred
   */
  private handleInitializationError(error: any): void {
    const formattingError = new FormattingError(
      FormattingErrorType.INITIALIZATION_ERROR,
      `Failed to initialize formatter: ${error.message}`,
      error
    );

    this.debugLogger('Initialization error:', formattingError);

    if (this.config.gracefulDegradation) {
      console.warn('[FormatterInit] Formatter failed to initialize, continuing without formatting', error);
      this.isInitialized = true; // Mark as initialized to prevent retry
    } else {
      throw formattingError;
    }
  }

  /**
   * Dispatch a formatting event
   * @param type Event type
   * @param target Target field (or null for module-level events)
   * @param data Additional event data
   */
  private dispatchEvent(type: string, target: FormattedFieldElement | null, data?: any): void {
    try {
      // For now, just log the event
      // In future tasks, this will dispatch actual events
      this.debugLogger(`Event: ${type}`, { target: target?.element, data });
    } catch (error) {
      this.debugLogger('Error dispatching event:', error);
    }
  }

  /**
   * Get the current configuration
   * @returns Current module configuration
   */
  public getConfig(): FormatterModuleConfig {
    return { ...this.config };
  }

  /**
   * Get currently tracked fields
   * @returns Array of formatted field elements
   */
  public getTrackedFields(): FormattedFieldElement[] {
    return Array.from(this.trackedFields.values());
  }

  /**
   * Check if the module is initialized
   * @returns Boolean indicating initialization status
   */
  public isModuleInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Clean up all event listeners and tracked fields
   * 
   * This method should be called if the formatter needs to be destroyed
   * or reinitialized.
   */
  public cleanup(): void {
    try {
      this.debugLogger('Cleaning up formatter');

      // Remove all event listeners
      this.eventListeners.forEach((listeners) => {
        listeners.forEach((cleanup) => cleanup());
      });

      // Clear tracking
      this.trackedFields.clear();
      this.eventListeners.clear();

      this.isInitialized = false;
      this.debugLogger('Formatter cleanup complete');

    } catch (error) {
      this.debugLogger('Error during cleanup:', error);
    }
  }
}